// My Data & Privacy: the user's rights under the Digital Personal Data Protection Act, 2023 —
// a copy of their data, correction, erasure / withdrawing consent, a nominee — and their
// complaints to the Grievance Officer.
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Paper, Stack, Button, TextField, Grid, Alert, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel, Link, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import {
  MY_DATA_EXPORT_QUERY, MY_DATA_REQUESTS_QUERY, REQUEST_DATA_ERASURE_MUTATION, REQUEST_DATA_CORRECTION_MUTATION,
  MY_NOMINEE_QUERY, SET_NOMINEE_MUTATION, MY_GRIEVANCES_QUERY,
} from '../graphql/queries';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const Section = ({ icon, title, children }) => (
  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
    <Stack direction="row" spacing={1} alignItems="center" mb={1}>{icon}<Typography variant="h6" fontWeight={700}>{title}</Typography></Stack>
    {children}
  </Paper>
);

export default function PrivacyCenterPage() {
  const [msg, setMsg] = useState(null);
  const [nominee, setNomineeForm] = useState({ name: '', relation: '', email: '', phone: '' });
  const [correction, setCorrection] = useState('');
  const [erase, setErase] = useState(null); // { reason, confirm }

  const { data: reqData, refetch: refetchReqs } = useQuery(MY_DATA_REQUESTS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: nomData } = useQuery(MY_NOMINEE_QUERY);
  const { data: grvData } = useQuery(MY_GRIEVANCES_QUERY, { fetchPolicy: 'cache-and-network' });
  useEffect(() => { if (nomData?.myNominee) setNomineeForm({ relation: '', email: '', phone: '', ...nomData.myNominee }); }, [nomData]);

  const ok = (text) => setMsg({ severity: 'success', text });
  const fail = (e) => setMsg({ severity: 'error', text: e.message });
  const [exportData, { loading: exporting }] = useLazyQuery(MY_DATA_EXPORT_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (d) => {
      const blob = new Blob([JSON.stringify(d.myDataExport, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `my-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      ok('Your data was downloaded.');
    },
    onError: fail,
  });
  const [saveNominee, { loading: savingNominee }] = useMutation(SET_NOMINEE_MUTATION, { onCompleted: () => ok('Nominee saved.'), onError: fail });
  const [requestCorrection] = useMutation(REQUEST_DATA_CORRECTION_MUTATION, { onCompleted: () => { setCorrection(''); refetchReqs(); ok('Correction request sent.'); }, onError: fail });
  const [requestErasure, { loading: erasing }] = useMutation(REQUEST_DATA_ERASURE_MUTATION, {
    onCompleted: () => { setErase(null); refetchReqs(); ok('Your request to close the account and erase your data was received. We will confirm by email.'); },
    onError: fail,
  });
  const requests = reqData?.myDataRequests || [];
  const grievances = grvData?.myGrievances || [];

  return (
    <Box>
      <Typography variant="h4" fontWeight={700}>My Data & Privacy</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Your rights under the Digital Personal Data Protection Act, 2023. See our <Link component={RouterLink} to="/legal/privacy">Privacy Policy</Link> for
        what we collect and why.
      </Typography>
      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      <Section icon={<DownloadIcon color="primary" />} title="Get a copy of your data">
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          Download everything we hold about you — account, profile, contacts, wishlists, budget, contracts, bookings, guests, invitations,
          messages, acceptances and complaints — as a file.
        </Typography>
        <Button variant="contained" startIcon={<DownloadIcon />} disabled={exporting} onClick={() => exportData()}>{exporting ? 'Preparing…' : 'Download my data'}</Button>
      </Section>

      <Section icon={<EditNoteIcon color="primary" />} title="Correct your data">
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          You can edit most details yourself on <Link component={RouterLink} to="/dashboard/profile">My Profile</Link>. For anything you can't change, tell us here.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField size="small" fullWidth placeholder="What needs correcting?" value={correction} onChange={e => setCorrection(e.target.value)} />
          <Button variant="outlined" disabled={!correction.trim()} onClick={() => requestCorrection({ variables: { details: correction } })}>Send</Button>
        </Stack>
      </Section>

      <Section icon={<PersonAddIcon color="primary" />} title="Nominate someone">
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          The person who may exercise your data rights if you die or become unable to (section 14 of the DPDP Act).
        </Typography>
        <Grid container spacing={1.5}>
          {[['name', 'Name'], ['relation', 'Relation'], ['email', 'Email'], ['phone', 'Phone']].map(([k, l]) => (
            <Grid item xs={12} sm={3} key={k}>
              <TextField size="small" fullWidth label={l} value={nominee[k] || ''} onChange={e => setNomineeForm(n => ({ ...n, [k]: e.target.value }))} />
            </Grid>
          ))}
        </Grid>
        <Button sx={{ mt: 1.5 }} variant="outlined" disabled={savingNominee} onClick={() => saveNominee({ variables: { input: nominee } })}>
          {nominee.name ? 'Save nominee' : 'Remove nominee'}
        </Button>
      </Section>

      <Section icon={<DeleteForeverIcon color="error" />} title="Withdraw consent and close your account">
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          We will close your account and erase your profile, photos, contacts, guests, invitations and identity documents. Records we must keep by
          law — contracts, invoices, payment records and the audit trail — are kept for the period the law requires.
        </Typography>
        <Button variant="outlined" color="error" startIcon={<DeleteForeverIcon />} onClick={() => setErase({ reason: '', confirm: false })}>Request account closure</Button>
      </Section>

      {requests.length > 0 && (
        <Section icon={<EditNoteIcon color="action" />} title="Your requests">
          <Table size="small">
            <TableHead><TableRow><TableCell>Request</TableCell><TableCell>Made</TableCell><TableCell>Status</TableCell><TableCell>Note</TableCell></TableRow></TableHead>
            <TableBody>
              {requests.map(r => (
                <TableRow key={r.id}>
                  <TableCell>{{ erasure: 'Close account & erase data', correction: 'Correction', other: 'Other' }[r.type] || r.type}</TableCell>
                  <TableCell>{fmt(r.createdAt)}</TableCell>
                  <TableCell><Chip size="small" label={r.status} color={r.status === 'completed' ? 'success' : r.status === 'rejected' ? 'error' : 'warning'} /></TableCell>
                  <TableCell>{r.note || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      )}

      <Section icon={<SupportAgentIcon color="primary" />} title="Complaints">
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          Raise a complaint with our Grievance Officer about the platform, a member, a vendor, a booking or your data.
        </Typography>
        <Button variant="contained" component={RouterLink} to="/legal/grievance" sx={{ mb: grievances.length ? 2 : 0 }}>Raise a complaint</Button>
        {grievances.length > 0 && (
          <Table size="small">
            <TableHead><TableRow><TableCell>Ticket</TableCell><TableCell>Subject</TableCell><TableCell>Raised</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
            <TableBody>
              {grievances.map(g => (
                <TableRow key={g.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{g.ticketNumber}</TableCell>
                  <TableCell>{g.subject}{g.resolution && <Typography variant="caption" color="text.secondary" display="block">{g.resolution}</Typography>}</TableCell>
                  <TableCell>{fmt(g.createdAt)}</TableCell>
                  <TableCell><Chip size="small" label={g.status.replace('_', ' ')} color={['resolved', 'closed'].includes(g.status) ? 'success' : 'warning'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Dialog open={Boolean(erase)} onClose={() => setErase(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Close your account?</DialogTitle>
        <DialogContent>
          {erase && (
            <Stack spacing={2} mt={1}>
              <Alert severity="warning">This cannot be undone. Open bookings with vendors should be cancelled or completed first.</Alert>
              <TextField label="Reason (optional)" value={erase.reason} onChange={e => setErase(x => ({ ...x, reason: e.target.value }))} fullWidth multiline minRows={2} />
              <FormControlLabel control={<Checkbox checked={erase.confirm} onChange={e => setErase(x => ({ ...x, confirm: e.target.checked }))} />}
                label="I want to withdraw my consent, close my account and have my personal data erased." />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErase(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!erase?.confirm || erasing} onClick={() => requestErasure({ variables: { reason: erase.reason } })}>Request closure</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
